'use client'

import React, { type ReactNode, useEffect, useState } from 'react'
import type { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'
import { cn } from '@lib/utils'

const LeftArrowImage = () => (
  <Image
    src='/assets/svg/carousel-button.svg'
    alt='previous item'
    width={20}
    height={200}
    className='flex-shrink-0'
  />
)

const RightArrowImage = () => (
  <Image
    src='/assets/svg/carousel-button.svg'
    alt='next item'
    width={20}
    height={200}
    className='flex-shrink-0 -scale-x-100'
  />
)

const CarouselButton = ({
  show = true,
  type,
  onClick,
  disabled = false,
  className,
}: {
  type: 'prev' | 'next'
  onClick: () => void
  show?: boolean
  disabled?: boolean
  className?: string
}) => {
  return (
    show && (
      <button
        disabled={disabled}
        className={cn(
          'flex px-4 xs:px-6',
          '[&:disabled]:cursor-not-allowed [&:disabled]:opacity-30',
          className,
          {
            embla__prev: type === 'prev',
            embla__next: type === 'next',
          },
        )}
        onClick={onClick}
      >
        {type === 'next' ? <RightArrowImage /> : <LeftArrowImage />}
      </button>
    )
  )
}

export const DeliveringCarousel = ({
  slides,
  title,
}: {
  slides: ReactNode[]
  title?: ReactNode | undefined
}) => {
  const [viewportRef, embla] = useEmblaCarousel({
    slidesToScroll: 3, // counter-acts delivering carousel having "slides" which are each two nodes/components <>{list}{card}</>
    // slidesToScroll: 'auto',
  })
  const [prevButtonDisabled, setPrevButtonDisabled] = useState(true)
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true)

  const scrollPrev = () => embla?.scrollPrev()
  const scrollNext = () => embla?.scrollNext()

  const toggleButtonsDisabled = (embla: EmblaCarouselType | undefined) => {
    setPrevButtonDisabled(!embla?.canScrollPrev())
    setNextButtonDisabled(!embla?.canScrollNext())
  }

  useEffect(() => {
    if (embla === undefined) {
      return
    }

    toggleButtonsDisabled(embla)
    embla.on('reInit', toggleButtonsDisabled)
    embla.on('select', toggleButtonsDisabled)

    return () => {
      embla.off('reInit', toggleButtonsDisabled)
      embla.off('select', toggleButtonsDisabled)
    }
  }, [embla])

  return (
    <>
      <div className='relative mb-5 flex items-center justify-between'>
        <CarouselButton
          disabled={prevButtonDisabled}
          type='prev'
          onClick={scrollPrev}
          className='xl:hidden'
        />
        {title != null && (
          <h2 className='mx-auto text-balance px-6 text-center text-3xl text-gold'>
            {title}
          </h2>
        )}
        <CarouselButton
          disabled={nextButtonDisabled}
          type='next'
          onClick={scrollNext}
          className='xl:hidden'
        />
      </div>
      <div
        className={cn(
          'embla',
          'relative h-full xl:ml-auto xl:mr-auto xl:max-w-[var(--max-width)]',
        )}
      >
        <div
          className={cn(
            'embla__viewport',
            'w-full overflow-x-hidden max-xl:pl-[5%] max-xl:pr-[5%]',
          )}
          ref={viewportRef}
        >
          <div
            className={cn(
              'embla_container',
              'grid grid-cols-[repeat(4,min(18rem,90vw))] grid-rows-[repeat(2,max-content)] items-center gap-x-0 gap-y-6',
            )}
          >
            {slides}
          </div>
        </div>
      </div>
    </>
  )
}
